import { forwardRef, useEffect, useState } from "react";
import Combobox from "./inputs/combobox";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { accountSiteList } from "store/appSelectors";
import { setLoading } from "store/appSlice";
import { DeviceListReqBody, DeviceTransferReqBody } from "api/types/device";
import { deviceList } from "api/device";
import CircleButton from "../controls/buttons/circle-button";
import {
  MdChevronLeft,
  MdChevronRight,
  MdKeyboardDoubleArrowLeft,
  MdKeyboardDoubleArrowRight,
} from "react-icons/md";
import DropdownButton from "../controls/buttons/dropdown";
import LabeledField from "providers/labeledField";
import { userAccounts } from "api/user";
import { Account } from "api/types/user";
import GenericButton from "../controls/buttons/generic";
import { siteListAccount } from "api/site";
import { t } from "i18next";

interface TransferWizardProps {
  onSubmit: (data: any) => void;
  resetTrigger?: number;
}

const TransferWizard = forwardRef<HTMLInputElement, TransferWizardProps>(
  ({ onSubmit, resetTrigger }, ref) => {
    const dispatch = useDispatch();
    const [disabled, setDisabled] = useState(true);
    const userInfo = useSelector(accountSiteList, shallowEqual);
    const [selectedSite, setSelectedSite] = useState();

    // Fetch site list
    const fetchSite = async (siteId: any) => {
      dispatch(setLoading(true));
      const body: DeviceListReqBody = {
        classId: siteId,
        sort: "snr",
        desc: true,
        installed: "BOTH",
      };

      const { data, status, isLoading } = await deviceList(body);
      dispatch(setLoading(isLoading));

      if (status && status === 200) {
        setSelectedSite(siteId);
        setAvailable(data);
        setSelectedAvailable([]);
      }
    };

    const [accounts, setAccounts] = useState<Account[]>([]);
    const [acc, setAcc] = useState<string>();

    const fetchAccounts = async () => {
      dispatch(setLoading(true));
      const { data, status, isLoading } = await userAccounts();
      dispatch(setLoading(isLoading));
      if (status === 200) {
        setAccounts(data?.accounts || []);
      }
    };

    const [transferSites, setTransferSites] = useState<any[]>([]);
    const [selectedTransferSite, setSelectedTransferSite] = useState();

    const fetchTransferSite = async (id: any) => {
      dispatch(setLoading(true));

      const { data, status, isLoading } = await siteListAccount(id);
      dispatch(setLoading(isLoading));

      if (status && status === 200) {
        setTransferSites(data);
      }
    };

    const [available, setAvailable] = useState([]);
    const [selected, setSelected] = useState([]);
    const [selectedAvailable, setSelectedAvailable] = useState<any[]>([]);
    const [selectedSelected, setSelectedSelected] = useState<any[]>([]);

    const move = (
      source: any[],
      targetSetter: React.Dispatch<React.SetStateAction<any[]>>,
      selectedItems: any[],
      sourceSetter: React.Dispatch<React.SetStateAction<any[]>>,
      clearSelection: React.Dispatch<React.SetStateAction<any[]>>
    ) => {
      targetSetter((prev) => [
        ...prev,
        ...selectedItems.filter(
          (item) => !prev.some((p) => p.snr === item.snr)
        ),
      ]);
      sourceSetter((prev) =>
        prev.filter(
          (item) => !selectedItems.some((sel) => sel.snr === item.snr)
        )
      );
      clearSelection([]);
    };

    const moveAll = (
      source: any[],
      targetSetter: React.Dispatch<React.SetStateAction<any[]>>,
      sourceSetter: React.Dispatch<React.SetStateAction<any[]>>,
      clearSelection: React.Dispatch<React.SetStateAction<any[]>>
    ) => {
      targetSetter((prev) => [
        ...prev,
        ...source.filter((item) => !prev.some((p) => p.snr === item.snr)),
      ]);
      sourceSetter([]);
      clearSelection([]);
    };

    useEffect(() => {
      fetchAccounts();
    }, []);

    const validate = () => {
      const body: DeviceTransferReqBody = {
        siteId: selectedTransferSite,
        accountId: acc,
        snrs: selected.map((item) => item.snr),
      };

      onSubmit(body);
    };

    useEffect(() => {
      if (acc && selectedTransferSite && selected.length > 0) {
        setDisabled(false);
      } else {
        setDisabled(true);
      }
    }, [acc, selectedTransferSite, selected]);

    useEffect(() => {
      setSelectedSite(undefined);
      setAvailable([]);
      setSelected([]);
      setSelectedAvailable([]);
      setSelectedSelected([]);
      setAcc(undefined);
      setAccounts([]);
      setSelectedTransferSite(undefined);
      setTransferSites([]);
      setDisabled(true);
    }, [resetTrigger]);

    return (
      <div className="mt-6 flex flex-col gap-4 rounded-lg">
        <div className="flex flex-col justify-center gap-4 rounded-xl bg-gray-100 md:flex-row md:items-center">
          {/* Left Box */}
          <div className="flex w-full flex-col gap-4 rounded-xl bg-gray-100 p-4  md:flex-row">
            <div className="min-w-64">
              <LabeledField
                label={t("common.select.site")}
                id={"transfer-wizard-site"}
                textColor="secondary"
              >
                <Combobox
                  filter
                  id="transfer-wizard-select-site"
                  placeholder={t("common.select.site")}
                  variant="solid"
                  color="secondary"
                  value={selectedSite}
                  onChange={(item) => {
                    fetchSite(item);
                  }}
                  items={userInfo}
                  labelKey="name"
                  valueKey="id"
                  sizes="md"
                  rounded="rounded-xl"
                />
              </LabeledField>
            </div>

            <div className="max-h-80 min-h-80 flex-1 overflow-y-scroll rounded border-2 p-2">
              {(available || selectedAvailable) &&
                available.map((item, index) => (
                  <div
                    className={`mb-1 ${
                      selectedAvailable.some((i) => i.snr === item.snr)
                        ? "rounded border-2 border-yellow-400 bg-yellow-100"
                        : ""
                    }`}
                  >
                    <DropdownButton
                      key={`${item.snr}`}
                      text={`${item.name} - [ ${item.snr} ]`}
                      onClick={() =>
                        setSelectedAvailable((prev) =>
                          prev.some((i) => i.snr === item.snr)
                            ? prev.filter((i) => i.snr !== item.snr)
                            : [...prev, item]
                        )
                      }
                    />
                  </div>
                ))}
            </div>
          </div>
          {/* Buttons */}
          <div className="flex min-w-10 justify-center gap-2 md:flex-col">
            <CircleButton
              onClick={() =>
                move(
                  available,
                  setSelected,
                  selectedAvailable,
                  setAvailable,
                  setSelectedAvailable
                )
              }
              icon={MdChevronRight}
              size="sm"
              extra="rounded-md justify-center"
              color="amber"
            />
            <CircleButton
              onClick={() =>
                move(
                  selected,
                  setAvailable,
                  selectedSelected,
                  setSelected,
                  setSelectedSelected
                )
              }
              icon={MdChevronLeft}
              size="sm"
              extra="rounded-md justify-center"
              color="amber"
            />
            <CircleButton
              onClick={() =>
                moveAll(
                  available,
                  setSelected,
                  setAvailable,
                  setSelectedAvailable
                )
              }
              icon={MdKeyboardDoubleArrowRight}
              size="sm"
              extra="rounded-md justify-center"
              color="amber"
            />
            <CircleButton
              onClick={() =>
                moveAll(
                  selected,
                  setAvailable,
                  setSelected,
                  setSelectedSelected
                )
              }
              icon={MdKeyboardDoubleArrowLeft}
              size="sm"
              extra="rounded-md justify-center"
              color="amber"
            />
          </div>
          {/* Right Box */}
          <div className="flex w-full flex-col gap-4 rounded-xl bg-gray-100 p-4 md:flex-row">
            <div className="max-h-80 min-h-80 flex-1  overflow-y-scroll rounded border-2 p-2">
              {(selected || selectedSelected) &&
                selected.map((item, index) => (
                  <div
                    className={`mb-1 ${
                      selectedSelected.some((i) => i.snr === item.snr)
                        ? "rounded border-2 border-yellow-400 bg-yellow-100"
                        : ""
                    }`}
                  >
                    <DropdownButton
                      key={`${item.snr}-${index}`}
                      text={`${item.name} - [ ${item.snr} ]`}
                      extra={
                        selectedSelected.some((i) => i.snr === item.snr)
                          ? "text-secondary"
                          : "text-text-800"
                      }
                      onClick={() =>
                        setSelectedSelected((prev) =>
                          prev.some((i) => i.snr === item.snr)
                            ? prev.filter((i) => i.snr !== item.snr)
                            : [...prev, item]
                        )
                      }
                    />
                  </div>
                ))}
            </div>

            <div className="flex min-w-64 flex-1 flex-col gap-4">
              <LabeledField
                label={t("button.action.transfer.account")}
                id={"transfer-wizard-account"}
                textColor="secondary"
              >
                <Combobox
                  filter
                  id="transfer-wizard-select-account"
                  placeholder={t("common.select.site")}
                  variant="solid"
                  color="secondary"
                  value={acc}
                  onChange={(item) => {
                    setAcc(item);
                    fetchTransferSite(item);
                  }}
                  items={accounts}
                  labelKey="name"
                  valueKey="id"
                  sizes="md"
                  rounded="rounded-xl"
                />
              </LabeledField>
              <LabeledField
                label={t("button.action.transfer.site")}
                id={"transfer-wizard-transfer-site"}
                textColor="secondary"
              >
                <Combobox
                  filter
                  id="transfer-wizard-select-transfer-site"
                  placeholder={t("common.select.site")}
                  variant="solid"
                  color="secondary"
                  value={selectedTransferSite}
                  onChange={(item) => {
                    setSelectedTransferSite(item);
                  }}
                  items={transferSites}
                  labelKey="name"
                  valueKey="id"
                  sizes="md"
                  rounded="rounded-xl"
                />
              </LabeledField>
            </div>
          </div>
        </div>
        <div className="flex justify-end py-2">
          <GenericButton
            text={t("button.action.transfer.devices")}
            onClick={validate}
            disabled={disabled}
          />
        </div>
      </div>
    );
  }
);

export default TransferWizard;
